import React, { useCallback, useState } from 'react';
import { searchCode, searchCodes } from '../../../actions/keyCodeActions';
import { NavLink } from 'react-router-dom';
import { Table, getVisibleColumns } from '../../common/Table';
import Loading from '../../common/Loading';
import KwPanel from '../../common/KwPanel';
import CodeStatusHistory from '../slideout/CodeStatusHistory';
import AutoSelectText from '../../common/AutoSelectText';
import {
  useTableSettings,
  getTableSettingsDefaults,
} from '../../../hooks/useTableSettings';
import SlidingPane from 'react-sliding-pane';
import CompanyLink from '../../common/links/CompanyLink';
import KeyProviderLink from '../../common/links/KeyProviderLink';
import Button from '../../common/Button';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

export interface SearchCodeListProps {
  reload?: any;
  code?: string;
  codesText?: string;
  bulkSearch?: boolean;
}

function SearchCodeList({ reload, code, codesText, bulkSearch }: SearchCodeListProps) {
  const allColumns = [
    'code',
    'keyProviderName',
    'tag1',
    'codeStatus',
    'gameName',
    'skuName',
    'requestReference',
    'companyName',
    'keyPool',
  ];
  const suffixColumns = ['id'];
  const { t } = useTranslation();
  const defaultVisibleColumns = allColumns;

  const [historySlideoutOpen, setHistorySlideoutOpen] = useState(false);
  const [historySlideoutItem, setHistorySlideoutItem] = useState<any>();

  const [codes, setCodes] = useState<any>();

  const loadList = useCallback(
    async (
      params: Parameters<typeof searchCode>[0] & {
        codesText?: string;
        bulkSearch?: boolean;
      }
    ) => {
      const { code: _code, codesText: _codesText, bulkSearch: _bulk, ...listParams } =
        params;
      try {
        if (bulkSearch) {
          return await searchCodes({ ...listParams, codesText });
        }
        return await searchCode({ ...listParams, code });
      } catch (e) {
        const msg =
          e instanceof Error ? (e.message ? e.message.trim() : '') : '';
        const isValidationMessage =
          msg.length > 0 &&
          !msg.startsWith('{') &&
          !msg.includes('An error occurred in the API');
        toast.error(
          isValidationMessage ? msg : t('Failed to search codes')
        );
        return {
          data: [],
          filteredRowCount: 0,
          totalRowCount: 0,
          page: 1,
          scopes: {},
        };
      }
    },
    [bulkSearch, code, codesText, t]
  );

  const { tableSettings, handleSetTableSettings: setTableSettings, filters, filterChange, isLoading } =
    useTableSettings(
      'reporteddicodesearchlistsabledlist-settings',
      getTableSettingsDefaults({
        defaultSort: 'codeStatus',
        direction: 'desc',
        defaultVisibleColumns,
      }),
      {
        code,
        codesText,
        bulkSearch,
      },
      loadList,
      setCodes,
      reload,
      t('Failed to search codes')
    );

  const handleShowCodeHistory = (e: React.MouseEvent, codeItem: any) => {
    (e as any).cancelBubble = true;
    setHistorySlideoutItem(codeItem);
    setHistorySlideoutOpen(true);
  };

  let columnsConfig = [
    {
      Key: 'code',
      Label: t('Key'),
      width: '18%',
      className: 'num no-wrap autoselect',
      Render: (item: any) => <AutoSelectText>{item.code}</AutoSelectText>,
    },
    {
      Key: 'keyProviderName',
      Label: t('Key provider'),
      width: '10%',
      Render: (item: any) => (
        <KeyProviderLink id={item.keyProviderId} name={item.keyProviderName} />
      ),
    },
    {
      Key: 'tag1',
      Label: t('Tag'),
      width: '8%',
      className: 'num autoselect',
      Render: (item: any) =>
        item.tag1 ? <AutoSelectText>{item.tag1}</AutoSelectText> : '--',
    },
    {
      Key: 'codeStatus',
      Label: t('Key status'),
      width: '10%',
      Render: (item: any) => item.codeStatus,
    },
    {
      Key: 'gameName',
      Label: t('Game'),
      width: '10%',
      Render: (item: any) => (
        <NavLink to={`/game/${item.gameId}`}>{item.gameName}</NavLink>
      ),
    },
    {
      Key: 'skuName',
      Label: t('SKU'),
      width: '10%',
      Render: (item: any) => (
        <NavLink to={`/sku/${item.skuId}`}>{item.skuName}</NavLink>
      ),
    },
    {
      Key: 'requestReference',
      Label: t('Request ref.'),
      width: '10%',
      Render: (item: any) => (
        <NavLink to={'/request/' + item.requestGroupId}>
          {item.requestReference}
        </NavLink>
      ),
    },
    {
      Key: 'companyName',
      Label: t('Company'),
      width: '10%',
      Render: (item: any) => (
        <CompanyLink id={item.companyId} name={item.companyName} />
      ),
    },
    {
      Key: 'keyPool',
      Label: t('Key pool'),
      width: '8%',
      Render: (item: any) => item.keyPool ?? '--',
    },
    {
      Key: 'id',
      Label: '',
      width: '6%',
      Sort: false,
      className: 'text-right',
      Render: (item: any) => (
        <Button
          className="btn btn-default"
          onClick={(e) => handleShowCodeHistory(e, item)}
          text={t('Code history')}
        />
      ),
    },
  ];

  const visibleColumns = tableSettings.columns ?? defaultVisibleColumns;
  const suffixColumnsWithConfig = getVisibleColumns(suffixColumns, columnsConfig);
  const columns = getVisibleColumns(visibleColumns, columnsConfig);

  return codes ? (
    <>
      <KwPanel className="mb-4">
        <Table
          columns={columns}
          suffixColumns={suffixColumnsWithConfig}
          allColumns={allColumns}
          rows={codes.data.map((x: any) => {
            return { Key: x.id, ...x };
          })}
          totalRowCount={codes.totalRowCount}
          filteredRowCount={codes.filteredRowCount}
          tableSettings={tableSettings}
          setTableSettings={setTableSettings}
          isLoading={isLoading}
          filterChange={filterChange}
        />
      </KwPanel>

      <SlidingPane
        isOpen={historySlideoutOpen}
        hideHeader={true}
        from="right"
        className="side-panel"
        onRequestClose={() => setHistorySlideoutOpen(false)}
      >
        <>
          {historySlideoutItem && (
            <CodeStatusHistory
              id={historySlideoutItem.id}
              done={() => {
                setHistorySlideoutOpen(false);
              }}
              cancel={() => setHistorySlideoutOpen(false)}
            />
          )}
        </>
      </SlidingPane>
    </>
  ) : (
    <Loading />
  );
}

export default SearchCodeList;
